/**
 * SocialNeedsScreenerPage — staff/CHW hub for the Social Needs Screener.
 *
 * Tabs:
 *  - Screen: pick a member and launch a screening; the questionnaire opens
 *    inline and, once submitted, the identified-needs summary is shown.
 *  - Try it: pick which domains to make positive -> POST /simulate -> render the
 *    needs summary (for demos / training, never real member data).
 *  - Safety: open interpersonal-safety events across administrations.
 *
 * Ships the AHC-HRSN core (CMS, public domain) — the no-cost alternative to the
 * licensed PRAPARE. All fetches go through the typed api client (authFetch).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardCheck,
  Play,
  FlaskConical,
  ShieldAlert,
  Loader2,
  ArrowLeft,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { MemberPicker, type PickedMember } from '@/components/behavioral-assessment/MemberPicker';
import { ScreenerQuestionnaire } from './ScreenerQuestionnaire';
import { NeedsSummary } from './NeedsSummary';
import { SafetyQueue } from './SafetyQueue';
import {
  socialNeedsScreenerApi,
  type InstrumentDetail,
  type InstrumentSummary,
  type DomainResult,
  type CreatedReferral,
} from './types';
import { useTabbedChrome } from '@/components/weavein';

const DEFAULT_CODE = 'AHC_HRSN';

type View =
  | { kind: 'hub' }
  | { kind: 'questionnaire'; administrationId: string; instrumentCode: string }
  | { kind: 'summary'; administrationId: string; referrals?: CreatedReferral[] };

export const SocialNeedsScreenerPage: React.FC = () => {
  const tabbed = useTabbedChrome();
  const [view, setView] = useState<View>({ kind: 'hub' });
  const [tab, setTab] = useState<'screen' | 'simulate' | 'safety'>('screen');

  // ── Full-screen sub-views ──────────────────────────────────────
  if (view.kind === 'questionnaire') {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => setView({ kind: 'summary', administrationId: view.administrationId })}
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back
        </Button>
        <ScreenerQuestionnaire
          administrationId={view.administrationId}
          instrumentCode={view.instrumentCode}
          onSubmitted={(id, referrals) => setView({ kind: 'summary', administrationId: id, referrals })}
        />
      </div>
    );
  }

  if (view.kind === 'summary') {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => setView({ kind: 'hub' })}
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to hub
        </Button>
        <ResultsView administrationId={view.administrationId} referrals={view.referrals} />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6">
      <div className="mb-4">
        {!tabbed && (
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <ClipboardCheck className="h-6 w-6" aria-hidden="true" />
          Social Needs Screener
        </h1>
        )}
        <p className="mt-1 text-sm text-muted-foreground">
          Screen members for social needs (housing, food, transportation, utilities, safety) and
          connect them to resources. Ships the AHC-HRSN core tool — no licensing cost.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="screen">
            <Play className="mr-2 h-4 w-4" aria-hidden="true" />
            Screen
          </TabsTrigger>
          <TabsTrigger value="simulate">
            <FlaskConical className="mr-2 h-4 w-4" aria-hidden="true" />
            Try it
          </TabsTrigger>
          <TabsTrigger value="safety">
            <ShieldAlert className="mr-2 h-4 w-4" aria-hidden="true" />
            Safety
          </TabsTrigger>
        </TabsList>

        <TabsContent value="screen" className="mt-4">
          <ScreenTab
            onLaunch={(administrationId, instrumentCode) =>
              setView({ kind: 'questionnaire', administrationId, instrumentCode })
            }
          />
        </TabsContent>

        <TabsContent value="simulate" className="mt-4">
          <SimulateTab />
        </TabsContent>

        <TabsContent value="safety" className="mt-4">
          <SafetyQueue />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ── Screen tab: pick a member, launch a screening ─────────────────
const ScreenTab: React.FC<{
  onLaunch: (administrationId: string, instrumentCode: string) => void;
}> = ({ onLaunch }) => {
  const [instrument, setInstrument] = useState<InstrumentSummary | null>(null);
  const [member, setMember] = useState<PickedMember | null>(null);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    socialNeedsScreenerApi
      .listInstruments()
      .then((r) => setInstrument(r.instruments.find((i) => i.code === DEFAULT_CODE) ?? r.instruments[0] ?? null))
      .catch(() => setInstrument(null));
  }, []);

  const launch = useCallback(async () => {
    if (!instrument) return;
    setLaunching(true);
    setError(null);
    try {
      const res = await socialNeedsScreenerApi.createAdministration({
        instrument_code: instrument.code,
        member_id: member?.id ?? null,
      });
      onLaunch(res.administration_id, instrument.code);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start screening.');
    } finally {
      setLaunching(false);
    }
  }, [instrument, member, onLaunch]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {instrument?.name ?? 'Screener'}
          {instrument && (
            <Badge variant="secondary">{instrument.total_items} items</Badge>
          )}
        </CardTitle>
        {instrument?.description && (
          <p className="text-sm text-muted-foreground">{instrument.description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="mb-1 block text-sm">Member (optional)</Label>
          <MemberPicker value={member} onChange={setMember} />
          <p className="mt-1 text-xs text-muted-foreground">
            Leave empty to run an unattached screening (e.g. a walk-up intake).
          </p>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <Button onClick={() => void launch()} disabled={launching || !instrument}>
          {launching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Play className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          Start screening
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Simulate tab: choose positive domains, render a needs summary ─
const SIM_DOMAINS: { code: string; label: string }[] = [
  { code: 'HOUSING', label: 'Housing' },
  { code: 'FOOD', label: 'Food' },
  { code: 'TRANSPORTATION', label: 'Transportation' },
  { code: 'UTILITIES', label: 'Utilities' },
  { code: 'SAFETY', label: 'Safety (interpersonal)' },
];

const SimulateTab: React.FC = () => {
  const [selected, setSelected] = useState<Set<string>>(new Set(['FOOD']));
  const [result, setResult] = useState<DomainResult[] | null>(null);
  const [isSim, setIsSim] = useState(false);
  const [running, setRunning] = useState(false);

  const toggle = (code: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });

  const run = useCallback(async () => {
    setRunning(true);
    try {
      const res = await socialNeedsScreenerApi.simulate({
        instrument_code: DEFAULT_CODE,
        positive_domains: [...selected],
        seed: 42,
      });
      setResult(res.domains);
      setIsSim(res.is_simulation);
    } catch {
      setResult([]);
    } finally {
      setRunning(false);
    }
  }, [selected]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="h-4 w-4" aria-hidden="true" />
            Try the screener with synthetic answers
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Choose which domains should come back as a need, then run. No real member data is used.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-2">
            {SIM_DOMAINS.map((d) => {
              const id = `sim-${d.code}`;
              return (
                <div key={d.code} className="flex items-center gap-3 rounded-md border p-3">
                  <Checkbox
                    id={id}
                    checked={selected.has(d.code)}
                    onCheckedChange={() => toggle(d.code)}
                  />
                  <Label htmlFor={id} className="cursor-pointer font-normal">
                    {d.label}
                  </Label>
                </div>
              );
            })}
          </div>
          <Button onClick={() => void run()} disabled={running}>
            {running ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <FlaskConical className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Run simulation
          </Button>
        </CardContent>
      </Card>

      {result && <NeedsSummary domains={result} isSimulation={isSim} />}
    </div>
  );
};

// ── Results view for a submitted administration ───────────────────
const ResultsView: React.FC<{ administrationId: string; referrals?: CreatedReferral[] }> = ({
  administrationId,
  referrals,
}) => {
  const [domains, setDomains] = useState<DomainResult[] | null>(null);
  const [isSim, setIsSim] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    socialNeedsScreenerApi
      .getResults(administrationId)
      .then((r) => {
        if (!cancelled) {
          setDomains(r.domains);
          setIsSim(r.is_simulation);
        }
      })
      .catch(() => {
        if (!cancelled) setDomains([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [administrationId]);

  const content = useMemo(() => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Loading results…
        </div>
      );
    }
    return <NeedsSummary domains={domains ?? []} isSimulation={isSim} referrals={referrals} />;
  }, [loading, domains, isSim, referrals]);

  return content;
};

export default SocialNeedsScreenerPage;
